import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { StoreDetails } from '@api/userAndStoreContext'

export enum VALID_DOMAINS {
    ALPHA = 'alpha',
    TEST = 'test',
    GAMMA = 'gamma',
    PROD = 'prod',
}

export enum VALID_REGIONS {
    IAD = 'iad',
    PDX = 'pdx',
    DUB = 'dub',
    SIN = 'sin',
}

export interface IStoreState {
    _currentDomain: string
    _currentRegion: string
    _currentDomainAndRegion: string
    _currentStore: string // Store that user has scanned into (active)
    _selectedStore: string // Store that user has selected but not scanned into yet
    _currentStoreInfo: any
    _currentCountryCode: string
    _storeList: StoreDetails[]
    _storeListLoaded: boolean
    _storeScanStatus: Record<string, boolean> // Track scan status per store
}

const initialState: IStoreState = {
    _currentDomain: undefined,
    _currentRegion: undefined,
    _currentDomainAndRegion: undefined,
    _currentStore: 'NO_CURRENT_STORE',
    _selectedStore: undefined,
    _currentStoreInfo: undefined,
    _currentCountryCode: undefined,
    _storeList: [],
    _storeListLoaded: false,
    _storeScanStatus: {},
}

export const storeSlice = createSlice({
    name: 'storeSlice',
    initialState,
    reducers: {
        setStoreId: (state, action: PayloadAction<string>) => { state._currentStore = action.payload },
        setSelectedStoreId: (state, action: PayloadAction<string>) => { state._selectedStore = action.payload },
        setStoreInfo: (state, action: PayloadAction<any>) => { state._currentStoreInfo = action.payload },
        setCurrentCountryCode: (state, action: PayloadAction<string>) => { state._currentCountryCode = action.payload },
        setCurrentDomainAndRegion: (state, action: PayloadAction<{ domain: string, region: string }>) => {
            state._currentDomain = action.payload.domain
            state._currentRegion = action.payload.region

            if (action.payload.region === VALID_REGIONS.IAD) {
                state._currentDomainAndRegion = action.payload.domain + '_' + action.payload.region
            } else {
                state._currentDomainAndRegion = action.payload.domain
            }

            // TODO: Add ihm.bindCoral() call
        },
        setStoreList: (state, action: PayloadAction<StoreDetails[]>) => {
            state._storeList = action.payload
            state._storeListLoaded = true
        },
        clearStoreList: (state) => {
            state._storeList = []
            state._storeListLoaded = false
        },
        /**
         * Scanning in is exclusive: an associate stands in one store, so a successful scan
         * invalidates every earlier one. Accumulating `true`s let a revisited store keep a stale
         * scan and suppressed its "Scan into store" prompt for the rest of the session.
         */
        setStoreScanStatus: (state, action: PayloadAction<{ storeId: string, scanned: boolean }>) => {
            if (action.payload.scanned) {
                state._storeScanStatus = { [action.payload.storeId]: true }
            } else {
                state._storeScanStatus[action.payload.storeId] = false
            }
        },
    },
})

export const {
    setStoreId,
    setSelectedStoreId,
    setStoreInfo,
    setCurrentCountryCode,
    setCurrentDomainAndRegion,
    setStoreList,
    clearStoreList,
    setStoreScanStatus,
} = storeSlice.actions

export default storeSlice.reducer
